import { isMosAuthProvider } from '../../commons/auth/session';
import { removeLocalStorageItem, setLocalStorageItem } from '../../commons/utils/localStorage';
import { user as userService } from '../user';

const CENTRAL_MODE_STORAGE_KEY = 'isCentralMode';

let refreshPromise;

/**
 * A MOS access token does not change when the default operator changes, but introspection
 * resolves another InPulse account. userId is stored once at login, so the next load would
 * request the previous client's account and get 403.
 *
 * Rewrites localStorage.userId and reloads only when the profile account differs, so a
 * second load cannot loop. The previous account's central-kitchen flag is cleared too:
 * Home only sets it and never turns it off, so the new client would keep the old workspace.
 * Legacy sessions skip the profile call.
 *
 * @param {string|null} storedUserId
 * @returns {Promise<boolean>} true when a reload was started and the caller must not fetch the stored user
 */
const refreshMosStoredAccountOnce = async (storedUserId) => {
  if (!isMosAuthProvider()) {
    return false;
  }

  let profile;

  try {
    profile = await userService.isAuthenticated();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[MOS] failed to read the current account before loading the user', error);

    return false;
  }

  const profileUserId = profile?.id;

  if (profileUserId === undefined || profileUserId === null || profileUserId === '') {
    return false;
  }

  const nextUserId = String(profileUserId);

  if (nextUserId === storedUserId) {
    return false;
  }

  try {
    await setLocalStorageItem('userId', nextUserId);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[MOS] failed to store the account for the current operator', error);

    return false;
  }

  // Do not reload if the write did not land: the next load would still see the old id and loop.
  if (window.localStorage.getItem('userId') !== nextUserId) {
    return false;
  }

  try {
    await removeLocalStorageItem(CENTRAL_MODE_STORAGE_KEY);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[MOS] failed to reset the workspace for the current operator', error);
  }

  window.location.reload();

  return true;
};

/**
 * One profile check per page load. App and Home both read localStorage.userId at startup;
 * a second call must not race the first and send the previous account id.
 *
 * @param {string|null} storedUserId
 * @returns {Promise<boolean>}
 */
export const refreshMosStoredAccount = (storedUserId) => {
  if (!isMosAuthProvider()) {
    return Promise.resolve(false);
  }

  if (!refreshPromise) {
    refreshPromise = refreshMosStoredAccountOnce(storedUserId);
  }

  return refreshPromise;
};
